import React, { useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const MessageThread = ({ messages, currentUser, isTyping = false }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderMessage = (message) => {
    const isCurrentUser = message.senderId === currentUser.id;
    
    return (
      <div
        key={message.id}
        className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`flex max-w-xs md:max-w-md ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2`}>
          {!isCurrentUser && (
            <Image
              src={message.senderAvatar}
              alt={message.senderName}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
            />
          )}
          
          <div className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`}>
            <div
              className={`px-4 py-2 rounded-2xl ${
                isCurrentUser
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground'
              }`}
            >
              {message.type === 'text' && (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              )}
              
              {message.type === 'file' && (
                <div className="flex items-center space-x-2">
                  <Icon name="Paperclip" size={16} />
                  <span className="text-sm">{message.fileName}</span>
                </div>
              )}
              
              {message.type === 'image' && (
                <div className="max-w-48">
                  <Image
                    src={message.imageUrl}
                    alt="Shared image"
                    className="rounded-lg object-cover"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-1 mt-1">
              <span className="text-xs text-muted-foreground">
                {formatTime(message.timestamp)}
              </span>
              {isCurrentUser && (
                <Icon
                  name={message.status === 'read' ? 'CheckCheck' : 'Check'}
                  size={12}
                  className={message.status === 'read' ? 'text-primary' : 'text-muted-foreground'}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-2">
      {messages.map(renderMessage)}
      
      {isTyping && (
        <div className="flex justify-start mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
              <Icon name="User" size={16} />
            </div>
            <div className="bg-muted px-4 py-2 rounded-2xl">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageThread;